<template>
  <div style="margin-bottom: 200px;">
    <div class="title">
      <h1 v-bg-typography="'h1'">Insights</h1>
      <bg-button
        v-if="!areInsightsLoading"
        postIcon="download"
        text="Insights PDF"
        @click="generateReport"
      ></bg-button>
    </div>
    <div class="insights-list">
      <router-link
        v-for="insight in insights"
        v-bind:key="insight.id"
        :to="`/test/${$route.params.id}/insights/${insight.id}`"
      >
        <div class="insights-list-item">
          <div class="insights-list-item__title">
            <h6>{{ `Insight #${insight.assigned_id}` }}</h6>
            <p v-html="insight.colourfulTitle"></p>
          </div>
          <div class="insights-list-item__separator"></div>
          <div class="insights-list-item__summary">
            <h6>Summary</h6>
            <p>
              <span v-html="insight.summary"></span>
            </p>
          </div>
          <div class="insights-list-item__separator"></div>
          <div class="insights-list-item__supporting-data">
            <h6>Supporting Data</h6>
            <p>{{ insight.significant_points.length }} data points</p>
            <p>{{ insight.numberOfParticipants }} participants</p>
          </div>
        </div>
      </router-link>
      <bg-loader
        :contentLoader="true"
        :dark="true"
        v-if="areInsightsLoading || insights.length === 0"
      ></bg-loader>
    </div>

    <div v-if="!areInsightsLoading">
      <vue-html2pdf
        :show-layout="false"
        :float-layout="true"
        :enable-download="true"
        :preview-modal="false"
        :filename="`Brainpeek User Test - ${testName}`"
        :pdf-quality="2"
        :manual-pagination="false"
        pdf-format="a4"
        pdf-orientation="landscape"
        pdf-content-width="100%"
        @hasStartedGeneration="hasStartedGeneration()"
        @hasGenerated="hasGenerated($event)"
        ref="html2Pdf"
      >
        <section slot="pdf-content">
          <div class="html2pdf__page-break">
            <section class="cover-slide ">
              <div class="cover-content">
                <p class="cover-title">Brainpeek Insights for</p>
                <p class="title">{{ testName }}</p>
                <p class="brand">{{ companyName }}</p>
                <p class="date">{{ getDate }}</p>
              </div>
            </section>
          </div>

          <div class="html2pdf__page-break">
            <section class="summary-slide html2pdf__page-break">
              <div class="left">
                <p class="title">Executive summary</p>
                <div class="scenario">
                  <p class="subtitle"><b>Scenario</b></p>
                  <p>
                    {{ this.testState.scenario }}
                  </p>
                </div>
                <div class="participants">
                  <p class="subtitle"><b>Target group</b></p>
                  <p v-if="this.testState.target_group === 'default'">
                    Dutch representative group of ten participant. Mixed in age,
                    gender and sex.
                  </p>
                  <p v-else>{{ this.testState.custom_target }}</p>
                </div>
              </div>
              <div class="right">
                <div
                  class="summary"
                  v-for="insight in insights"
                  :key="insight.id"
                >
                  <p>
                    <span
                      >{{ insight.assigned_id }}.
                      <b><span v-html="insight.colourfulTitle"></span></b
                    ></span>
                  </p>

                  <p v-html="insight.summary"></p>
                </div>
              </div>
            </section>
          </div>

          <div class="slide " v-for="insight in insights" :key="insight.id">
            <div class="html2pdf__page-break">
              <section class="pdf-item">
                <div>
                  <p class="title">
                    <span>
                      {{ insight.assigned_id }}.
                      <span v-html="insight.colourfulTitle"></span
                    ></span>
                  </p>
                  <div v-if="insight.highlighted_significant_point">
                    <div class="highlight">
                      <img :src="insight.highlight_url" alt="" />
                      <p>
                        <i
                          ><b
                            >Participant
                            {{
                              insight.highlighted_significant_point
                                .participant_id
                            }}:
                          </b>
                          "{{
                            getComment(
                              insight.highlighted_significant_point.comment
                            )
                          }}"</i
                        >
                      </p>
                    </div>
                  </div>
                  <div>
                    <div class="eeg">
                      <div class="icon">
                        <svg
                          width="23"
                          height="25"
                          viewBox="0 0 23 25"
                          fill="0B0B3D"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M23 12.0148C23 11.8943 22.989 11.7738 22.9781 11.6533C22.9781 11.6205 22.9671 11.5876 22.9671 11.5548C22.9562 11.4562 22.9452 11.3686 22.9233 11.27C22.9233 11.2481 22.9124 11.2152 22.9124 11.1933C22.8905 11.0838 22.8576 10.9743 22.8357 10.8757C22.8248 10.8319 22.8138 10.799 22.8029 10.7552C22.781 10.6786 22.7481 10.6019 22.7152 10.5252C22.7043 10.4924 22.6933 10.4595 22.6714 10.4267C22.6276 10.3281 22.5838 10.2295 22.529 10.131C22.5181 10.0981 22.4962 10.0762 22.4852 10.0433C22.4414 9.96667 22.3976 9.89 22.3429 9.81333C22.3319 9.79143 22.31 9.76952 22.299 9.73667C22.2333 9.6381 22.1676 9.55048 22.091 9.45191C22.091 9.45191 22.091 9.4519 22.08 9.44095C22.0033 9.35333 21.9267 9.26571 21.85 9.17809C21.839 9.16714 21.8281 9.15619 21.8171 9.14524C21.6419 8.98095 21.4667 8.81667 21.2586 8.68524C21.2476 8.67429 21.2367 8.66333 21.2148 8.66333C21.1271 8.60857 21.0395 8.55381 20.941 8.49905C20.93 8.4881 20.9081 8.47714 20.8971 8.47714C20.8862 8.46619 20.8643 8.45524 20.8533 8.45524C21.1162 7.94048 21.2586 7.36 21.2586 6.76857C21.2586 4.70952 19.5938 3.04476 17.5567 3.03381C17.2281 1.30333 15.7057 0 13.8986 0C13.0114 0 12.1681 0.317619 11.5 0.887143C10.8319 0.317619 9.97762 0 9.10143 0C7.29429 0 5.7719 1.30333 5.45429 3.05571C3.40619 3.05571 1.75238 4.73143 1.75238 6.79048C1.75238 7.3819 1.89476 7.95143 2.15762 8.47714C2.12476 8.47714 2.11381 8.4881 2.10286 8.49905C2.08095 8.49905 2.07 8.51 2.0481 8.52095C1.96048 8.57571 1.8619 8.63048 1.77429 8.68524C1.76333 8.69619 1.75238 8.70714 1.74143 8.71809C1.54429 8.84952 1.3581 9.00286 1.18286 9.17809C1.1719 9.18905 1.16095 9.2 1.15 9.21095C1.06238 9.29857 0.985714 9.37524 0.909048 9.47381C0.909048 9.47381 0.909048 9.47381 0.898095 9.48476C0.832381 9.56143 0.766667 9.66 0.700952 9.75857C0.679048 9.78048 0.668095 9.80238 0.657143 9.82429C0.602381 9.90095 0.558571 9.97762 0.514762 10.0543C0.492857 10.0871 0.481905 10.109 0.470952 10.1419C0.41619 10.2405 0.372381 10.3281 0.328571 10.4376C0.306667 10.4705 0.295714 10.5033 0.284762 10.5362C0.251905 10.6129 0.23 10.6786 0.208095 10.7552C0.18619 10.799 0.175238 10.8319 0.164286 10.8757C0.131429 10.9852 0.109524 11.0838 0.087619 11.1933C0.087619 11.2152 0.0766667 11.2371 0.0766667 11.27C0.0547619 11.3686 0.0438095 11.4562 0.0328571 11.5548C0.0328571 11.5876 0.0219048 11.6205 0.0219048 11.6533C0.0109524 11.7738 0 11.8943 0 12.0148C0 13.4276 0.744762 14.72 1.91667 15.4319C1.64286 15.9576 1.48952 16.549 1.48952 17.1624C1.48952 19.1776 3.08857 20.8205 5.07095 20.8862C5.32286 22.6933 6.85619 24.0952 8.71809 24.0952C9.81333 24.0952 10.7771 23.7557 11.5 23.1533C12.2229 23.7667 13.1976 24.0952 14.2819 24.0952C16.1438 24.0952 17.6771 22.6933 17.9181 20.8862C19.9114 20.8205 21.4995 19.1776 21.4995 17.1624C21.4995 16.549 21.3571 15.9686 21.0724 15.4319C22.2662 14.72 23 13.4276 23 12.0148ZM11.04 5.9581C10.7005 6.19905 10.1529 6.29762 9.58333 6.1881C8.84952 6.05667 8.24714 5.61857 8.00619 5.04905C7.89667 4.8081 7.63381 4.69857 7.39286 4.80809C7.16286 4.90667 7.05333 5.18048 7.15191 5.41048C7.52429 6.27571 8.37857 6.91095 9.41905 7.09714C9.62714 7.14095 9.83524 7.15191 10.0433 7.15191C10.3938 7.15191 10.7333 7.09714 11.04 6.98762V22.321C10.4705 22.8686 9.66 23.1643 8.71809 23.1643C7.20667 23.1643 5.98 21.9267 5.98 20.4152C5.98 20.4152 5.98 20.4152 5.98 20.4043C5.98 19.6814 6.25381 19.0571 6.76857 18.5971C7.27238 18.1481 7.96238 17.8962 8.72905 17.8962C8.98095 17.8962 9.18905 17.6881 9.18905 17.4362C9.18905 17.1843 8.98095 16.9762 8.72905 16.9762C8.3019 16.9762 7.89667 17.0419 7.51333 17.1624C7.63381 16.4176 7.53524 15.651 7.19571 14.9719C7.08619 14.7419 6.81238 14.6433 6.58238 14.7529C6.35238 14.8624 6.25381 15.1471 6.37429 15.3771C6.73571 16.111 6.73571 16.9871 6.39619 17.7319C6.31952 17.7867 6.24286 17.8524 6.16619 17.9181C5.57476 18.4548 5.20238 19.1667 5.10381 19.9662C3.62524 19.9005 2.44238 18.6738 2.44238 17.1733C2.44238 16.5819 2.62857 16.0124 2.9681 15.5305L3.28571 15.0595L2.76 14.8295C1.64286 14.3367 0.92 13.2414 0.92 12.0148C0.92 11.9162 0.930952 11.8286 0.930952 11.73C0.930952 11.6971 0.941905 11.6643 0.941905 11.6314C0.952857 11.5657 0.963809 11.511 0.974762 11.4452C0.985714 11.4124 0.985714 11.3795 0.996667 11.3357C1.00762 11.281 1.02952 11.2152 1.04048 11.1605C1.05143 11.1276 1.05143 11.0948 1.06238 11.0729C1.09524 11.0071 1.11714 10.9195 1.15 10.8429C1.16095 10.81 1.1719 10.7881 1.19381 10.7552C1.21571 10.7005 1.23762 10.6457 1.27048 10.591C1.28143 10.5581 1.30333 10.5252 1.32524 10.5033C1.3581 10.4486 1.39095 10.3938 1.42381 10.35C1.43476 10.3281 1.44571 10.2952 1.45667 10.2733C1.56619 10.131 1.67571 9.98857 1.80714 9.85714C1.8181 9.84619 1.82905 9.83524 1.84 9.83524C1.89476 9.78048 1.96048 9.72571 2.01524 9.67095C2.02619 9.64905 2.0481 9.63809 2.05905 9.62714C2.12476 9.57238 2.17952 9.52857 2.24524 9.48476C2.25619 9.47381 2.26714 9.46286 2.2781 9.46286C2.43143 9.36429 2.59571 9.27667 2.77095 9.2C2.79286 9.18905 2.82571 9.17809 2.84762 9.16714C3.1981 9.02476 3.58143 8.9481 3.97571 8.9481C5.00524 8.9481 5.87048 9.4519 6.42905 10.35C5.85952 10.6457 5.36667 11.0729 4.99429 11.6095C4.8519 11.8176 4.89571 12.1133 5.10381 12.2557C5.18048 12.3105 5.27905 12.3433 5.36667 12.3433C5.50905 12.3433 5.65143 12.2776 5.75 12.1462C6.40714 11.2043 7.51333 10.7114 8.64143 10.8648C8.89333 10.8976 9.12333 10.7224 9.15619 10.4705C9.18905 10.2076 9.01381 9.97762 8.7619 9.94476C8.25809 9.87905 7.76524 9.9119 7.30524 10.0214C6.59333 8.7619 5.37762 8.01714 3.97571 8.01714C3.64714 8.01714 3.32952 8.06095 3.0119 8.13762C2.7819 7.72143 2.66143 7.26143 2.66143 6.77952C2.66143 5.23524 3.91 3.97571 5.45429 3.97571C5.56381 3.97571 5.67333 3.98667 5.79381 3.99762L6.28667 4.06333L6.31952 3.57048C6.40714 2.0919 7.62286 0.930952 9.10143 0.930952C9.82429 0.930952 10.5252 1.21571 11.04 1.71952V5.9581ZM20.24 14.8295L19.7143 15.0595L20.0538 15.5305C20.4043 16.0124 20.5795 16.5819 20.5795 17.1733C20.5795 18.6738 19.3967 19.9005 17.9181 19.9662C17.7976 18.9914 17.2938 18.1152 16.5271 17.5129C16.8448 17.0748 17.0638 16.571 17.1624 16.0233C17.2062 15.7714 17.0419 15.5305 16.79 15.4867C16.5381 15.4429 16.2971 15.6071 16.2533 15.859C16.0562 16.9543 15.2019 17.8414 14.1176 18.0714C13.8657 18.1262 13.7124 18.3671 13.7562 18.619C13.8 18.8381 13.9971 18.9914 14.2052 18.9914C14.2381 18.9914 14.271 18.9914 14.3038 18.9805C14.8952 18.86 15.4429 18.5862 15.8919 18.2029C16.5929 18.7176 17.02 19.539 17.02 20.4262C17.02 21.9486 15.7933 23.1752 14.2819 23.1752C13.34 23.1752 12.5295 22.8795 11.96 22.3319V13.3838C12.1681 13.3948 12.431 13.3948 12.7157 13.3948C13.0771 13.3948 13.4495 13.3838 13.7671 13.351C13.8657 13.7781 14.0081 14.1614 14.2162 14.4462C14.3038 14.5667 14.4462 14.6324 14.5886 14.6324C14.6871 14.6324 14.7748 14.5995 14.8624 14.5448C15.0705 14.3914 15.1143 14.1067 14.961 13.8986C14.5667 13.351 14.4681 12.0038 14.6433 11.5657C14.7419 11.3248 14.6214 11.0619 14.3914 10.9633C14.1505 10.8648 13.8876 10.9852 13.789 11.2262C13.6686 11.5219 13.6248 11.96 13.6467 12.4419C13.2086 12.4857 12.5186 12.4857 11.971 12.4638V6.16619V1.71952C12.4857 1.21571 13.1867 0.930952 13.9095 0.930952C15.2567 0.930952 16.3848 1.89476 16.6367 3.17619C15.8919 3.36238 15.2129 3.77857 14.709 4.38095C14.5448 4.5781 14.5667 4.87381 14.7638 5.03809C14.8514 5.11476 14.961 5.14762 15.0595 5.14762C15.191 5.14762 15.3224 5.09286 15.41 4.98333C15.87 4.43571 16.5162 4.09619 17.2062 4.00857C17.3267 3.99762 17.4362 3.98667 17.5457 3.98667C19.079 3.98667 20.3386 5.24619 20.3386 6.79048C20.3386 7.27238 20.2181 7.73238 19.9881 8.14857C19.6814 8.07191 19.3638 8.0281 19.0243 8.0281C18.3781 8.0281 17.7538 8.18143 17.1843 8.4881C16.801 8.12667 16.3519 7.8419 15.8481 7.64476C15.6071 7.54619 15.3443 7.66667 15.2567 7.90762C15.169 8.14857 15.2895 8.41143 15.5305 8.49905C16.6038 8.90429 17.3486 9.85714 17.48 11.0071C17.5129 11.2481 17.71 11.4124 17.94 11.4124C17.9619 11.4124 17.9729 11.4124 17.9948 11.4124C18.2467 11.3795 18.4329 11.1495 18.4 10.8976C18.3233 10.2843 18.1152 9.71476 17.7976 9.21095C18.181 9.03571 18.6081 8.9481 19.0352 8.9481C19.4295 8.9481 19.8129 9.02476 20.1633 9.16714C20.1852 9.17809 20.2181 9.18905 20.24 9.2C20.4152 9.27667 20.5686 9.36429 20.7219 9.46286C20.7329 9.47381 20.7438 9.48476 20.7548 9.48476C20.8205 9.52857 20.8862 9.57238 20.941 9.62714C20.9519 9.63809 20.9738 9.64905 20.9848 9.66C21.0505 9.71476 21.1052 9.76952 21.16 9.82429C21.171 9.83524 21.1819 9.84619 21.1819 9.84619C21.3133 9.97762 21.4229 10.12 21.5324 10.2733C21.5433 10.2952 21.5652 10.3171 21.5762 10.339C21.609 10.3938 21.6419 10.4376 21.6748 10.4924C21.6967 10.5252 21.7076 10.5471 21.7295 10.58C21.7514 10.6348 21.7843 10.6895 21.8062 10.7443C21.8171 10.7771 21.839 10.799 21.85 10.8319C21.8829 10.9086 21.9157 10.9962 21.9376 11.0729C21.9486 11.1057 21.9595 11.1386 21.9595 11.1605C21.9814 11.2152 21.9924 11.281 22.0033 11.3357C22.0143 11.3686 22.0143 11.4014 22.0252 11.4452C22.0362 11.5 22.0471 11.5657 22.0581 11.6314C22.0581 11.6643 22.069 11.6971 22.069 11.73C22.08 11.8286 22.08 11.9162 22.08 12.0148C22.08 13.2305 21.3571 14.3367 20.24 14.8295Z"
                            fill="#0B0B3D"
                          />
                        </svg>
                      </div>
                      <div>
                        <div class="htmltext">
                          <p v-html="insight.eeg_comment"></p>
                        </div>
                      </div>
                    </div>
                    <div class="eye" v-if="insight.eye_tracking_comment != ''">
                      <div class="icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="feather feather-eye"
                        >
                          <path
                            d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                          ></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </div>
                      <div>
                        <div class="htmltext">
                          <p v-html="insight.eye_tracking_comment"></p>
                        </div>
                      </div>
                    </div>
                    <div
                      class="touch"
                      v-if="insight.touch_tracking_comment != ''"
                    >
                      <div class="icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#0B0B3D"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="feather feather-smartphone"
                        >
                          <rect
                            x="5"
                            y="2"
                            width="14"
                            height="20"
                            rx="2"
                            ry="2"
                          ></rect>
                          <line x1="12" y1="18" x2="12.01" y2="18"></line>
                        </svg>
                      </div>
                      <div>
                        <div class="htmltext">
                          <p v-html="insight.touch_tracking_comment"></p>
                        </div>
                      </div>
                    </div>
                    <div class="behavior">
                      <div class="icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z"
                            stroke="#0B0B3D"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M13 13L19 19"
                            stroke="#0B0B3D"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                      <div>
                        <div class="htmltext">
                          <p v-html="insight.behavior_comment"></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style="display: flex; flex-direction: column; align-content: space-between;"
                >
                  <div class="explanation">
                    <p class="subtitle"><b>Explanation</b></p>
                    <div class="htmltext">
                      <p v-html="insight.explanation"></p>
                    </div>
                  </div>
                  <p>
                    <i>Suggestions for this insight are on the next page..</i>
                  </p>
                </div>
              </section>
            </div>

            <div class="html2pdf__page-break">
              <section class="pdf-item ">
                <div>
                  <p class="subtitle"><b>Suggestions</b></p>
                  <div
                    class="htmltext"
                    v-if="
                      insight.suggestion != '' &&
                        insight.suggestion != '<p></p>'
                    "
                  >
                    <p v-html="insight.suggestion"></p>
                  </div>
                  <div
                    style="margin: 10px 0;"
                    class="htmltext"
                    v-for="suggestion in suggestions.filter(
                      (el) => el.insight_id === insight.id
                    )"
                    :key="suggestion.id"
                  >
                    <div v-if="insight.id === suggestion.insight_id">
                      <p
                        style="font-weight: 600;"
                        v-html="suggestion.title"
                      ></p>
                      <br />
                      <p v-html="suggestion.suggestion"></p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <section class="html2pdf__page-break">
            <div class="logo-slide">
              <p class="closing">
                For the full interactive report, go to:<br />
                <b>brainpeek.braingineers.com</b>
              </p>
              <img src="@/assets/imgs/logo.png" alt="" />
            </div>
          </section>
        </section>
      </vue-html2pdf>
    </div>
  </div>
</template>

<style lang="scss">
.pdf-item {
  .htmltext ul {
    list-style-position: inside;
    padding-left: 10px;
    li p {
      position: relative;
      left: -13px;
    }
  }

  .htmltext {
    p {
      display: inline;
    }
  }
}
</style>

<style scoped lang="scss">
.title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cover-slide {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1125px;
  height: 792px;

  .cover-content {
    text-align: center;
    max-width: 800px;

    .cover-title {
      font-weight: bold;
      font-size: 24pt;
    }
    .title {
      font-size: 40pt;
      color: #0931f0;
      margin-bottom: 40px;
      font-weight: 600;
    }
    .brand {
      font-size: 16pt;
      font-weight: 600;
    }
    .date {
      font-size: 16pt;
    }
  }
}

.summary-slide {
  display: grid;
  grid-template-columns: 30% 70%;
  gap: 30px;
  width: 100%;
  height: 792px;
  padding-top: 1px;

  .left {
    background-color: #ebebef;
    padding-top: 50px;
    padding-right: 50px;
    padding-bottom: 50px;
    padding-left: 50px;
    height: 100%;
  }

  .right {
    padding-top: 50px;
    padding-right: 50px;
    padding-bottom: 50px;
    padding-left: 0;
  }

  .title {
    font-size: 20pt;
    font-weight: bold;
    padding-bottom: 30px;
    border-bottom: 3px solid white;
    margin-bottom: 20px;
  }

  .subtitle {
    font-size: 14pt;
    margin-bottom: 5px;
  }
  .summary {
    margin: 10px 0;
  }

  .scenario {
    margin: 20px 0;
  }
}
.pdf-item {
  padding: 50px;
  display: grid;
  grid-template-columns: 50% 50%;
  gap: 30px;
  width: 100%;
  height: 100%;
  position: relative;

  .title {
    font-size: 20pt;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .highlight {
    display: flex;
    margin: 10px 0;
    padding-bottom: 25px;
    border-bottom: 2px solid #ebebef;

    img {
      width: 267;
      height: 150px;
      margin-right: 10px;
    }
  }

  .eeg,
  .eye,
  .behavior,
  .touch {
    margin: 15px 0;
    display: flex;

    .icon {
      display: flex;
      align-items: flex-start;
      margin-right: 15px;
    }

    .subtitle {
      font-size: 14pt;
      margin-bottom: 5px;
    }
  }

  .explanation,
  .suggestion {
    margin: 15px 0;
    .subtitle {
      font-size: 14pt;
      margin-bottom: 5px;
    }
  }
}

.logo-slide {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 1125px;
  height: 792px;

  img {
    width: 250px;
  }

  .closing {
    font-size: 18pt;
    text-align: center;
    margin-bottom: 50px;
  }
}

.insights-list-item {
  width: 100%;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 25px;
  display: flex;
  flex-direction: row;
  margin-top: 20px;
}

.insights-list-item:hover {
  box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
  cursor: pointer;
}

.insights-list-item h6 {
  margin-bottom: 15px;
  font-size: 12px;
  color: #636383;
}

.insights-list-item__title {
  width: 30%;
  line-height: 21px;
  letter-spacing: 0.5px;
}
.insights-list-item__title > p {
  color: #0b0b3d;
  font-weight: bold;
  margin-right: 20px;
}

.insights-list-item__summary {
  width: 50%;
  height: 100%;
  padding: 0 20px 15px 20px;
  line-height: 21px;
  letter-spacing: 0.5px;
}

.insights-list-item__supporting-data {
  padding: 0 0 0 20px;
  width: 19%;
  height: 100%;
  line-height: 21px;
  letter-spacing: 0.5px;
}

.insights-list-item__separator {
  border-left: 2px solid #ebebef;
  min-height: 85px;
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgLoader from "@/components/bg-loader";
import API from "@/api";
import VueHtml2pdf from "vue-html2pdf";
import BgIcon from "@/components/bg-icon";
import { mapState } from "vuex";
import BgButton from "@/components/bg-button";

export default {
  name: "insights",
  components: {
    BgLoader,
    VueHtml2pdf,
    BgIcon,
    BgButton,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      insights: [],
      areInsightsLoading: true,
      pdfOptions: {
        enableLinks: true,
        filename: "Brainpeek User Test",
        jsPDF: {
          unit: "in",
          format: "a4",
          orientation: "landscape",
        },
        html2canvas: {
          scale: 1,
          useCORS: true,
        },
        margin: 0,
        image: {
          type: "pdf",
          quality: 2,
        },
      },
      suggestions: [],
    };
  },
  computed: {
    ...mapState({
      testState: (state) => state.test,
      userState: (state) => state.user,
    }),
    testName() {
      return (this.testState && this.testState.name) ? this.testState.name : "";
    },
    companyName() {
      return (this.testState && this.testState.company && this.testState.company.name)
        ? this.testState.company.name
        : "";
    },
    getDate: function() {
      const updated = this.testState && this.testState.updated_at;
      if (!updated) return "";
      let date = new Date(updated * 1000);
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${months[date.getMonth()]} ${date.getFullYear()}`;
    },
  },
  watch: {
    "$route.params.id": function(id) {
      this.fetchData();
    },
  },
  methods: {
    async fetchData() {
      this.areInsightsLoading = true;
      const testId = this.$route.params.id;
      // Ensure the test details are present in the store when landing directly on Insights
      if (!this.testState || (this.testState && parseInt(testId) !== this.testState.id)) {
        try {
          await this.$store.dispatch("fetchTest", testId);
        } catch (e) {
          // ignore; insights can still load independently
        }
      }
      API.test.getInsights(testId).then((res) => {
        this.insights = res.data.insights.map((insight) => {
          return {
            ...insight,
            numberOfParticipants: insight.significant_points
              .map((sp) => {
                return sp.participant_id;
              })
              .filter((value, index, self) => {
                return self.indexOf(value) === index;
              }).length,
            colourfulTitle: insight.title.replace(
              /joy|attention|frustration/gi,
              this.highlightWords
            ),
          };
        });
        this.areInsightsLoading = false;
      });
      const suggestions = await API.test.getSuggestions(this.$route.params.id);
      this.suggestions = suggestions.suggestions;
    },
    generateReport() {
      this.$refs.html2Pdf.generatePdf();
    },
    getComment(comment) {
      if (comment.length > 190) {
        return comment.substring(0, 190).concat(" ...");
      } else {
        return comment;
      }
    },
    highlightWords(word) {
      let result;
      switch (word.toUpperCase()) {
        case "JOY":
          result = `<span style="color: var(--bg-color-green)">${word}</span>`;
          break;
        case "FRUSTRATION":
          result = `<span style="color: var(--bg-color-pink)">${word}</span>`;
          break;
        case "ATTENTION":
          result = `<span style="color: var(--bg-color-yellow)">${word}</span>`;
          break;
        case "AANDACHT":
          result = `<span style="color: var(--bg-color-yellow)">${word}</span>`;
          break;
        case "BLIJDSCHAP":
          result = `<span style="color: var(--bg-color-green)">${word}</span>`;
          break;
        case "FRUSTRATIE":
          result = `<span style="color: var(--bg-color-pink)">${word}</span>`;
          break;
      }
      return result;
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
